import { useEffect, useRef, useState } from "react";
import { Icon } from "./icons";

type VideoTestimonial = {
  id: string;
  name: string;
  flag: string;
  src: string;
};

const videoTestimonials: readonly VideoTestimonial[] = [
  {
    id: "luke",
    name: "Luke",
    flag: "🇦🇺",
    src: "/assets/testimonials/luke.mp4?v=21407e6",
  },
  {
    id: "jay",
    name: "Jay",
    flag: "🌎",
    src: "/assets/testimonials/jay.mp4?v=21407e6",
  },
];

function VideoTestimonialCard({ testimonial }: { testimonial: VideoTestimonial }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasSource, setHasSource] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || hasSource) return;

        video.src = testimonial.src;
        video.load();
        setHasSource(true);
        observer.disconnect();
      },
      { rootMargin: "320px 0px", threshold: 0.01 },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [hasSource, testimonial.src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver !== "undefined") return;

    video.src = testimonial.src;
    video.load();
    setHasSource(true);
  }, [testimonial.src]);

  const handleReady = () => {
    setIsReady(true);
    setHasError(false);
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
      void video.play().catch(() => undefined);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video || !isReady) return;

    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setIsMuted(nextMuted);
    if (nextMuted && video.paused) void video.play().catch(() => undefined);
  };

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video || !isReady) return;

    if (video.paused) {
      void video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  };

  return (
    <figure className={`funnel-video-testimonial${isReady ? " is-ready" : ""}${hasError ? " has-error" : ""}`}>
      <div className="funnel-video-testimonial__media">
        <video
          ref={videoRef}
          className="funnel-video-testimonial__video"
          aria-label={`${testimonial.name}'s AgentMax video`}
          data-video-src={testimonial.src}
          autoPlay
          loop
          muted={isMuted}
          playsInline
          preload="none"
          onCanPlay={handleReady}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onError={() => setHasError(true)}
          onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
        />

        <div className="funnel-video-testimonial__skeleton" aria-hidden="true">
          <span className="funnel-video-testimonial__skeleton-glow" />
          <span className="funnel-video-testimonial__skeleton-mark"><Icon name="play" /></span>
          <span className="funnel-video-testimonial__skeleton-copy">Loading customer clip</span>
        </div>

        {hasError ? (
          <div className="funnel-video-testimonial__error" role="status">
            This clip is unavailable in the local preview.
          </div>
        ) : null}

        <button
          type="button"
          className="funnel-video-testimonial__play"
          aria-label={isPlaying ? `Pause ${testimonial.name}'s video` : `Play ${testimonial.name}'s video`}
          title={isPlaying ? `Pause ${testimonial.name}'s video` : `Play ${testimonial.name}'s video`}
          aria-pressed={isPlaying}
          disabled={!isReady}
          onClick={togglePlayback}
        >
          <Icon name={isPlaying ? "pause" : "play"} />
        </button>

        <button
          type="button"
          className="funnel-video-testimonial__sound"
          aria-label={isMuted ? `Turn on sound for ${testimonial.name}'s video` : `Mute ${testimonial.name}'s video`}
          title={isMuted ? `Turn on sound for ${testimonial.name}'s video` : `Mute ${testimonial.name}'s video`}
          aria-pressed={!isMuted}
          disabled={!isReady}
          onClick={toggleMute}
        >
          <Icon name={isMuted ? "volumeOff" : "volume"} />
        </button>
      </div>

      <figcaption className="funnel-video-testimonial__caption">
        <span className="funnel-video-testimonial__identity">
          <strong><span aria-hidden="true">{testimonial.flag}</span> {testimonial.name}</strong>
          <span className="funnel-video-testimonial__badge">Verified user</span>
        </span>
      </figcaption>
    </figure>
  );
}

export function FunnelVideoTestimonials() {
  return (
    <section className="funnel-video-testimonials" aria-label="Customer video testimonials">
      <div className="funnel-video-testimonials__header">
        <p className="section-kicker">Customer proof</p>
        <h3>See AgentMax in the wild.</h3>
      </div>
      <div className="funnel-video-testimonials__rail">
        {videoTestimonials.map((testimonial) => (
          <VideoTestimonialCard key={testimonial.id} testimonial={testimonial} />
        ))}
      </div>
    </section>
  );
}
